import { api } from './client';

/**
 * Custom Focus API
 *
 * Endpoints for managing custom focus areas in Focus sessions.
 * Part of the Focus Mode Enhancement feature (Phase B).
 */
export const customFocusApi = {
  /**
   * List custom focus areas available for a story.
   * Returns both story-level and project-level focuses.
   * @param {string} storyId - Story UUID
   * @returns {Promise<{focuses: Array, count: number}>}
   */
  listForStory: (storyId) => api.get(`/stories/${storyId}/custom-focuses`),

  /**
   * List project-level custom focus areas (series altitude).
   */
  listForProject: (projectId) => api.get(`/projects/${projectId}/custom-focuses`),

  /**
   * Create a story-level custom focus area.
   * @param {string} storyId - Story UUID
   * @param {Object} data - Focus data
   * @param {string} data.name - Name of the focus area (max 100 chars)
   * @param {string} [data.description] - Optional description
   * @param {string} [data.exploration_mode='develop'] - One of: brainstorm, develop, define, test
   * @param {string} [data.areas_to_explore] - Optional hints for areas to explore
   * @param {string} [data.icon='pencil'] - Lucide icon name
   * @returns {Promise<Object>} Created focus area
   */
  createForStory: (storyId, data) => api.post(`/stories/${storyId}/custom-focuses`, data),

  /**
   * Create a project-level custom focus area.
   * @param {string} projectId - Project UUID
   * @param {Object} data - Focus data (same as createForStory)
   * @returns {Promise<Object>} Created focus area
   */
  createForProject: (projectId, data) => api.post(`/projects/${projectId}/custom-focuses`, data),

  /**
   * Get a specific custom focus area.
   * @param {string} focusId - Focus area UUID
   * @returns {Promise<Object>} Focus area data
   */
  get: (focusId) => api.get(`/custom-focuses/${focusId}`),

  /**
   * Update a custom focus area.
   * @param {string} focusId - Focus area UUID
   * @param {Object} data - Fields to update
   * @returns {Promise<Object>} Updated focus area
   */
  update: (focusId, data) => api.put(`/custom-focuses/${focusId}`, data),

  /**
   * Delete a custom focus area.
   * @param {string} focusId - Focus area UUID
   * @returns {Promise<void>}
   */
  delete: (focusId) => api.delete(`/custom-focuses/${focusId}`),
};

// Export individual functions for convenience
export const fetchCustomFocuses = customFocusApi.listForStory;
export const createCustomFocus = (data, scope, targetId) => {
  return scope === 'story'
    ? customFocusApi.createForStory(targetId, data)
    : customFocusApi.createForProject(targetId, data);
};
export const updateCustomFocus = customFocusApi.update;
export const deleteCustomFocus = customFocusApi.delete;
